import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  InjectionToken,
  input,
  output,
  viewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
// RCP-441: wishlist disabled — WishlistService removed from import
import { BreakpointObserverService, ConfigurationService, OrderLimitService } from '@core';
import { LightCarouselComponent } from '@light-carousel';
import { isDiscontedPrice, ProductDetails, usdToPoints } from '@models';
import { SkeletonTemplateService } from '@skeleton-loader';
import { AccessibleClickDirective } from '../directives/accessible-click.directive';
import { DigitsGroupingPipe } from '../pipes/digits-grouping.pipe';
import { ORDER_LIMIT_MESSAGES } from '../constants/order-limit-messages';

export const PRODUCT_LIST_CARD_PARAMS = new InjectionToken<{ isDisplayBrandNameLabel?: boolean }>(
  'PRODUCT_LIST_CARD_PARAMS',
);

@Component({
  selector: 'lib-product-list-card',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    LightCarouselComponent,
    AccessibleClickDirective,
    DigitsGroupingPipe,
    MatTooltipModule,
  ],
  templateUrl: './product-list-card.component.html',
  styleUrl: './product-list-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductListCardComponent {
  protected readonly rewardName = inject(ConfigurationService).rewardName;
  product = input.required<ProductDetails>();
  isOutOfStock = input<boolean>(false);
  /**
   * FCR 19.1 — presentation flag: the monthly per-SKU limit has been reached for this product.
   * Disables "Add to Cart" and shows the limit message under the card. Logic wired separately.
   */
  orderLimitReached = input<boolean>(false);
  orderLimitMessage = ORDER_LIMIT_MESSAGES.limitReached;
  private orderLimitService = inject(OrderLimitService);

  hoverOnAction = output<boolean>();
  // RCP-441: wishlist disabled
  // toggleWishlist = output<ProductDetails>();
  addToCart = output<ProductDetails>();
  clickOnProduct = output<ProductDetails>();

  readonly lightCarousel = viewChild<ElementRef>('lightCarousel');

  skeletonService = inject(SkeletonTemplateService);
  isMobile = inject(BreakpointObserverService).isMobile;
  // RCP-441: wishlist disabled
  // wishlistService = inject(WishlistService);
  isDisplayBrandNameLabel = inject(PRODUCT_LIST_CARD_PARAMS, { optional: true })?.isDisplayBrandNameLabel ?? true;

  vm = computed(() => {
    const product = this.product();
    const price = product.price;
    const discountedPrice = product.discountedPrice;

    const pricePoints = usdToPoints(price);
    const discountedPricePoints = discountedPrice ? usdToPoints(discountedPrice) : 0;

    return {
      product: product,
      smallImagesForCards: (product as ProductDetails)?.images,
      price: price,
      priceInPoints: pricePoints,
      discountedPrice: discountedPrice,
      discountedPoints: discountedPricePoints,
      isOutOfStock: this.isOutOfStock(),
      // AC01 — restrict when the SKU is already in the cart at the monthly limit (input as override).
      orderLimitReached: this.orderLimitReached() || this.orderLimitService.isSkuAtLimit(product.productId),
    };
  });
  // RCP-441: wishlist disabled
  // isInWishList = computed(() => this.wishlistService.wishlistProductIds()?.includes(this.vm().product?.productId ?? 0));
  private isDiscountAvailable = computed(() => isDiscontedPrice(this.product()));

  ariaLabelAddToCart = computed(() => {
    const productName = this.product().name;
    const discountText = this.isDiscountAvailable() ? 'discount' : '';
    const priceText = Math.trunc(this.product().discountedPrice || this.product().price);
    return `Add to cart ${productName} with ${discountText} price ${priceText} ${this.rewardName()}`;
  });

  // RCP-441: wishlist disabled
  // toggleWishList(): void {
  //   this.toggleWishlist.emit(this.vm().product!);
  // }

  focus(): void {
    this.lightCarousel()?.nativeElement?.focus();
  }
}
